import { BlocksSnippetAri } from '@atlassian/ari';

import { ADFEntity } from '@atlaskit/adf-utils/types';
import { cfetch } from '@confluence/network';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

import {
	type Snippet,
	SnippetBodyFormat,
	type SnippetOwner,
	type SnippetProduct,
	type SnippetStatus,
} from '../snippetTypes';
import { parseSnippetContent } from '../snippetUtils';

const COMMON_HEADERS = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
};

const BLOCK_SERVICE_API_URL = '/gateway/api/blocks/v1';
const SNIPPETS_PATH = '/snippets';
const PRODUCT_PATH = '/product/confluence';

export class SnippetError extends Error {
	constructor(readonly status: number) {
		super(`Snippet error: ${status}`);
	}
}

export type SnippetData = {
	resourceId: string; // ARI of the Snippet
	snippetId: string;
	name: string; // User defined name
	description?: string; // User defined name
	body: string; // String of the type defined in `bodyFormat` (example: "ADF")
	bodyFormat: SnippetBodyFormat;
	product: SnippetProduct;
	label?: string;
	isFavorite: 0 | 1;
	status: SnippetStatus;
	createdBy: string;
	createdAt: string; // String of integer timestamp
	updatedAt: string; // String of integer timestamp
};

const transformSnippets = async (snippets: SnippetData[]): Promise<Snippet[]> => {
	const { displayName } = await getSessionData();
	return snippets.map((snippet: SnippetData) => transformSnippetData(snippet, displayName));
};

const transformSnippetData = (snippet: SnippetData, displayName: string | null): Snippet => {
	const createdBy: SnippetOwner = {
		accountId: snippet.createdBy,
		displayName:
			snippet.createdBy?.toLowerCase() === 'atlassian'
				? 'Atlassian'
				: displayName || snippet.createdBy,
	};

	return {
		ari: BlocksSnippetAri.parse(snippet.resourceId),
		snippetId: snippet.snippetId,
		name: snippet.name,
		description: snippet.description,
		bodyFormat: snippet.bodyFormat,
		body: parseSnippetContent(snippet.body),
		product: snippet.product,
		isFavorite: snippet.isFavorite,
		status: snippet.status,
		createdBy,
		createdAt: new Date(Number(snippet.createdAt)),
		updatedAt: new Date(Number(snippet.updatedAt)),
	};
};

export type CreateSnippetBody = {
	name: string;
	body: ADFEntity;
	bodyFormat: SnippetBodyFormat;
	product: SnippetProduct;
};

/**
 * Fetch snippets from the Block Service for Confluence. https://bitbucket.org/atlassian/block-service/src/main/
 * Please note that no error handling is done. Ensure you are handling bad response codes when using this.
 */
export const fetchSnippets = async (): Promise<Snippet[]> => {
	const params = new URLSearchParams({
		format: SnippetBodyFormat.ADF,
	});

	const response = await cfetch(
		`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH + PRODUCT_PATH}?${params.toString()}`,
		{
			method: 'GET',
			headers: COMMON_HEADERS,
		},
	);

	if (!response.ok) {
		throw new SnippetError(response.status);
	}

	const responseData = await response.json();
	if (!responseData.success) {
		return [];
	}

	// Success and error can both be present, so we need to log the error if it is present
	if (Array.isArray(responseData.error) && responseData.error.length > 0) {
		responseData.error.forEach((error: any) => {
			const errorToLog = new Error(
				`Error when fetching snippets: ${error?.message || JSON.stringify(error)}`,
			);
			getMonitoringClient().submitError(errorToLog, {
				attribution: Attribution.INTEGRATIONS,
			});
		});
	}

	return transformSnippets(responseData.success);
};

export const createSnippet = async (params: CreateSnippetBody): Promise<SnippetData> => {
	const response = await cfetch(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}`, {
		method: 'POST',
		headers: COMMON_HEADERS,
		body: JSON.stringify(params),
	});

	if (!response.ok) {
		throw new SnippetError(response.status);
	}

	return response.json();
};
